import {
  ADS_CAMPAIGN_META_DESTINATION_TYPE_LEAD_INSTANT_FORM_OPTION,
  LINKEDIN,
  META,
} from "./adsPlatformIds.js";
import { prepareRecommendedLeadFormCreate } from "./leadFormInitialValues.js";

export const GENERATE_RECOMMENDED_LEAD_FORM_TIMEOUT_MS = 90000;
export const GENERATE_RECOMMENDED_LEAD_FORM_RETRY_DELAY_MS = 5000;

const MAX_RECOMMENDATION_ADS = 50;
const MAX_AD_NAME_LENGTH = 80;
const MAX_AD_TEXTS = 3;
const MAX_AD_TEXT_LENGTH = 160;
const MAX_DESTINATION_URL_LENGTH = 2000;
const MAX_AD_FORMAT_LENGTH = 80;

const LEAD_FORM_RECOMMENDATION_FAILED = "leadFormRecommendationFailed";
const LEAD_FORM_RECOMMENDATION_IN_PROGRESS = "leadFormRecommendationInProgress";
const CAMPAIGN_IS_GENERATING = "campaignIsGenerating";
const MISSING_PARAMETERS = "missingParameters";

const EXPECTED_LEAD_FORM_RECOMMENDATION_CODES = new Set([
  "unauthorized",
  MISSING_PARAMETERS,
  "forbidden",
  "notHaveEnoughUsageValue",
  "notHaveEnoughBrandUsageValue",
  "notFound",
  CAMPAIGN_IS_GENERATING,
  LEAD_FORM_RECOMMENDATION_IN_PROGRESS,
]);

const EXPECTED_LEAD_FORM_RECOMMENDATION_STATUSES = new Set([
  400, 401, 402, 403, 404, 409,
]);

// Same outcomes shouldReportErrorToSentry drops for any request.
const EXPECTED_TRANSPORT_CODES = new Set([
  "ERR_NETWORK",
  "ERR_CANCELED",
  "ECONNABORTED",
  "ETIMEDOUT",
]);

const SAFE_CODE = /^[A-Za-z0-9_.:-]{1,80}$/;
const SAFE_PLATFORM = new Set([META, LINKEDIN]);

const inFlightRequests = new Map();

const hasText = (value) => typeof value === "string" && value.trim().length > 0;

const entityKeyOf = (entityID) =>
  entityID == null || entityID === "" ? "" : String(entityID);

const entriesOf = (value) => {
  if (Array.isArray(value)) return value.map((item, index) => [index, item]);
  if (value && typeof value === "object") return Object.entries(value);
  return [];
};

// Same resolution and mapper as the Crear card (#1950), so the editor only
// asks the backend when the card would also open with defaults.
export const storedLeadFormInitialValues = ({
  platform,
  recommendations,
  adID,
  adsGroups,
  language,
} = {}) =>
  prepareRecommendedLeadFormCreate({
    platform,
    recommendations,
    entityID: adID,
    adsGroups: platform === META ? adsGroups : undefined,
    language,
  }).initialValues;

const recommendedLeadFormGroupsPath = (platform) =>
  `platformsData.${platform}.recommendations.leadFormGroups`;

// Recommendations live only under platformsData.{platform}.recommendations.
export const writeRecommendedLeadFormGroups = ({
  platform,
  leadFormGroups,
  persisted,
  setValue,
} = {}) => {
  if (typeof setValue !== "function" || !Array.isArray(leadFormGroups)) {
    return null;
  }
  if (platform !== META && platform !== LINKEDIN) return null;
  const path = recommendedLeadFormGroupsPath(platform);
  setValue(path, leadFormGroups, {
    shouldDirty: persisted !== true,
  });
  return path;
};

const truncateText = (value, max) => {
  if (typeof value !== "string") return "";
  return value.trim().slice(0, max).trim();
};

const textValues = (items) => {
  const next = [];
  for (const item of Array.isArray(items) ? items : []) {
    const raw = typeof item === "string" ? item : item?.value;
    const text = truncateText(raw, MAX_AD_TEXT_LENGTH);
    if (!text) continue;
    next.push(text);
    if (next.length >= MAX_AD_TEXTS) break;
  }
  return next;
};

const languageCodeOf = (language) => {
  const rawLanguage = Array.isArray(language) ? language[0] : language;
  const value =
    rawLanguage && typeof rawLanguage === "object"
      ? rawLanguage.value
      : rawLanguage;
  if (typeof value !== "string") return "";
  const raw = value.trim();
  if (!raw) return "";
  const urn = raw.match(/locale:([a-z]{2})_/i);
  if (urn) return urn[1].toLowerCase();
  const short = raw.split(/[-_]/)[0].toLowerCase();
  return /^[a-z]{2}$/.test(short) ? short : "";
};

const campaignDestinationURL = ({ campaign, platform }) => {
  const fromPlatform = campaign?.platformsData?.[platform]?.destinationURL;
  if (hasText(fromPlatform)) {
    const url = fromPlatform.trim();
    return url.length <= MAX_DESTINATION_URL_LENGTH ? url : "";
  }
  const fromCampaign = campaign?.destinationURL;
  if (hasText(fromCampaign)) {
    const url = fromCampaign.trim();
    return url.length <= MAX_DESTINATION_URL_LENGTH ? url : "";
  }
  return "";
};

const destinationUrlOf = (entity) => {
  if (hasText(entity?.destinationUrl)) {
    const url = entity.destinationUrl.trim();
    return url.length <= MAX_DESTINATION_URL_LENGTH ? url : "";
  }
  const urls = Array.isArray(entity?.destinationUrls)
    ? entity.destinationUrls
    : [];
  for (const item of urls) {
    const raw = typeof item === "string" ? item : item?.value;
    if (!hasText(raw)) continue;
    const url = raw.trim();
    return url.length <= MAX_DESTINATION_URL_LENGTH ? url : "";
  }
  return "";
};

const adPayload = ({ ad, adGroupID }) => {
  if (!ad?.id) return null;
  const payload = {
    id: String(ad.id),
    name: truncateText(ad.name, MAX_AD_NAME_LENGTH),
    titles: textValues(ad.titles),
    headlines: textValues(ad.headlines),
    descriptions: textValues(ad.descriptions),
  };
  const groupID = entityKeyOf(ad.adGroupID || adGroupID);
  if (groupID) payload.adGroupID = groupID;
  return payload;
};

const collectMetaAds = ({ adsGroups, adID }) => {
  const ads = [];
  let current = null;
  entriesOf(adsGroups).forEach(([, adGroup]) => {
    entriesOf(adGroup?.ads).forEach(([, ad]) => {
      const payload = adPayload({ ad, adGroupID: adGroup?.id });
      if (!payload) return;
      const isCurrent = entityKeyOf(ad?.id) === entityKeyOf(adID);
      if (isCurrent) {
        current = {
          payload,
          destinationURL: destinationUrlOf(ad),
          destinationType: adGroup?.destinationType || "",
          adFormat: truncateText(ad?.adFormat, MAX_AD_FORMAT_LENGTH),
        };
        return;
      }
      ads.push(payload);
    });
  });
  const ordered = current ? [current.payload, ...ads] : ads;
  return {
    ads: ordered.slice(0, MAX_RECOMMENDATION_ADS),
    destinationURL: current?.destinationURL || "",
    destinationType: current?.destinationType || "",
    adFormat: current?.adFormat || "",
  };
};

const collectLinkedInAds = ({ assetsGroups, adID, adFormat }) => {
  const ads = [];
  let current = null;
  entriesOf(assetsGroups).forEach(([, assetsGroup]) => {
    const payload = adPayload({
      ad: assetsGroup,
      adGroupID: assetsGroup?.id,
    });
    if (!payload) return;
    const isCurrent = entityKeyOf(assetsGroup?.id) === entityKeyOf(adID);
    if (isCurrent) {
      current = {
        payload,
        destinationURL: destinationUrlOf(assetsGroup),
      };
      return;
    }
    ads.push(payload);
  });
  const ordered = current ? [current.payload, ...ads] : ads;
  return {
    ads: ordered.slice(0, MAX_RECOMMENDATION_ADS),
    destinationURL: current?.destinationURL || "",
    destinationType: "",
    adFormat: truncateText(adFormat, MAX_AD_FORMAT_LENGTH),
  };
};

export const buildGenerateRecommendedLeadFormBody = ({
  campaign,
  platform,
  adID,
  adGroupID,
  advertiserID,
  campaignID,
  language,
} = {}) => {
  if (platform !== META && platform !== LINKEDIN) return null;
  const advertiser = entityKeyOf(advertiserID);
  const campaignKey = entityKeyOf(campaignID);
  if (!advertiser || !campaignKey) return null;

  const collected =
    platform === LINKEDIN
      ? collectLinkedInAds({
          assetsGroups: campaign?.assetsGroups,
          adID,
          adFormat: campaign?.platformsData?.[LINKEDIN]?.adFormat,
        })
      : collectMetaAds({
          adsGroups: campaign?.adsGroups,
          adID,
        });

  const body = {
    advertiserID: advertiser,
    campaignID: campaignKey,
    platform,
  };
  const languageCode = languageCodeOf(language);
  if (languageCode) body.language = languageCode;
  const currentAdID = entityKeyOf(adID);
  if (currentAdID) body.adID = currentAdID;
  if (collected.ads.length > 0) body.ads = collected.ads;
  const currentAd = body.ads?.find((ad) => ad.id === currentAdID);
  const groupID = entityKeyOf(adGroupID);
  if (currentAd && !currentAd.adGroupID && groupID) {
    currentAd.adGroupID = groupID;
  }
  const destinationURL =
    collected.destinationURL || campaignDestinationURL({ campaign, platform });
  if (destinationURL) body.destinationURL = destinationURL;
  if (
    platform === META &&
    collected.destinationType ===
      ADS_CAMPAIGN_META_DESTINATION_TYPE_LEAD_INSTANT_FORM_OPTION
  ) {
    body.destinationType =
      ADS_CAMPAIGN_META_DESTINATION_TYPE_LEAD_INSTANT_FORM_OPTION;
  }
  if (collected.adFormat) body.adFormat = collected.adFormat;
  return body;
};

const codeOf = (value) => (typeof value === "string" ? value : "");

const readHttpSuccess = (response) => {
  const status = response?.status ?? 0;
  const body = response?.data;
  if (body?.ok === true && body.data && typeof body.data === "object") {
    return { ok: true, status, data: body.data };
  }
  return { ok: false, status, code: codeOf(body?.code) };
};

const readHttpFailure = (error) => {
  const status = error?.response?.status;
  return {
    ok: false,
    status: typeof status === "number" ? status : 0,
    code: codeOf(error?.response?.data?.code) || codeOf(error?.code),
  };
};

const sleepFor = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const executeRecommendedLeadFormRequest = async ({
  post,
  body,
  sleep,
  timeoutMs,
  retryDelayMs,
}) => {
  const attempt = async () => {
    try {
      return readHttpSuccess(await post({ body, timeout: timeoutMs }));
    } catch (error) {
      return readHttpFailure(error);
    }
  };

  let result = await attempt();
  if (result.code === LEAD_FORM_RECOMMENDATION_IN_PROGRESS) {
    await sleep(retryDelayMs);
    result = await attempt();
  }
  return result;
};

export const resetRecommendedLeadFormRequestsForTests = () => {
  inFlightRequests.clear();
};

// Every editor of the same campaign and platform (list and Crear modals, any
// ad) shares one request while it is in flight. The first body wins.
export const requestRecommendedLeadForm = ({
  key,
  post,
  body,
  sleep = sleepFor,
  timeoutMs = GENERATE_RECOMMENDED_LEAD_FORM_TIMEOUT_MS,
  retryDelayMs = GENERATE_RECOMMENDED_LEAD_FORM_RETRY_DELAY_MS,
} = {}) => {
  const existing = inFlightRequests.get(key);
  if (existing) return existing;

  const promise = executeRecommendedLeadFormRequest({
    post,
    body,
    sleep,
    timeoutMs,
    retryDelayMs,
  }).finally(() => {
    if (inFlightRequests.get(key) === promise) {
      inFlightRequests.delete(key);
    }
  });
  inFlightRequests.set(key, promise);
  return promise;
};

const safeCode = (code) =>
  typeof code === "string" && SAFE_CODE.test(code)
    ? code
    : LEAD_FORM_RECOMMENDATION_FAILED;

// Only the code, status and platform reach Sentry. The response message can
// carry brand or form text, so it is never read.
export const leadFormRecommendationSentryReport = ({
  code,
  status,
  platform,
} = {}) => {
  const normalizedCode = safeCode(code);
  if (EXPECTED_LEAD_FORM_RECOMMENDATION_CODES.has(normalizedCode)) return null;
  if (EXPECTED_TRANSPORT_CODES.has(normalizedCode)) return null;
  if (EXPECTED_LEAD_FORM_RECOMMENDATION_STATUSES.has(status)) return null;
  return {
    code: normalizedCode,
    platform: SAFE_PLATFORM.has(platform) ? platform : null,
    status: typeof status === "number" && status > 0 ? status : null,
  };
};

// When the response only carries the new group, keep the other stored groups.
const groupsToStore = ({ data, recommendations }) => {
  if (Array.isArray(data?.leadFormGroups) && data.leadFormGroups.length > 0) {
    return data.leadFormGroups;
  }
  const group = data?.group;
  if (!group || typeof group !== "object") return null;
  const current = Array.isArray(recommendations?.leadFormGroups)
    ? recommendations.leadFormGroups
    : [];
  const others = current.filter(
    (item) => group.key == null || item?.key !== group.key
  );
  return [...others, group];
};

const noticeForFailure = (result) =>
  result?.code === CAMPAIGN_IS_GENERATING ? "campaignGenerating" : "failed";

export const loadRecommendedLeadForm = async ({
  platform,
  adID,
  adsGroups,
  language,
  recommendations,
  request,
  setValue,
  shouldApply = () => true,
} = {}) => {
  const stored = storedLeadFormInitialValues({
    platform,
    recommendations,
    adID,
    adsGroups,
    language,
  });
  if (stored) {
    return {
      requested: false,
      initialValues: stored,
      notice: null,
      report: null,
    };
  }

  let response;
  try {
    response = await request();
  } catch (error) {
    response = { ok: false, status: 0, code: codeOf(error?.code) };
  }

  if (!response?.ok) {
    return {
      requested: true,
      initialValues: null,
      notice: noticeForFailure(response),
      report: leadFormRecommendationSentryReport({
        code: response?.code,
        status: response?.status,
        platform,
      }),
    };
  }

  const leadFormGroups = groupsToStore({
    data: response.data,
    recommendations,
  });
  if (shouldApply()) {
    writeRecommendedLeadFormGroups({
      platform,
      leadFormGroups,
      persisted: response.data?.persisted,
      setValue,
    });
  }

  const initialValues =
    storedLeadFormInitialValues({
      platform,
      recommendations: { leadFormGroups: leadFormGroups || [] },
      adID,
      adsGroups,
      language,
    }) ||
    storedLeadFormInitialValues({
      platform,
      recommendations: { leadForm: response.data?.group?.leadForm },
      language,
    });
  if (!initialValues) {
    return {
      requested: true,
      initialValues: null,
      notice: "failed",
      report: null,
    };
  }

  return {
    requested: true,
    initialValues,
    notice: null,
    report: null,
  };
};

export const reportLeadFormRecommendationFailure = ({
  dispatch,
  launchError,
  report,
} = {}) => {
  if (!report || typeof dispatch !== "function") return;
  if (typeof launchError !== "function") return;

  const error = new Error("generateRecommendedLeadForm failed");
  error.code = report.code;
  dispatch(
    launchError(error, false, [], {
      functionName: "generateRecommendedLeadForm",
      context: {
        platform: report.platform,
        code: report.code,
        status: report.status,
      },
      tags: {
        feature: "leadFormRecommendation",
        platform: report.platform,
        code: report.code,
      },
      level: "error",
    })
  );
};
